import React, { useContext, useEffect, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';
import { Button, Loader, Modal } from 'semantic-ui-react';

import '../../css/PresentationOptionsModal.less';

import { SatCoreComponent, SatCoreRegister } from '../../SatCoreRegistry';

export const PRESENTATION_OPTION = {
  SAVE_FOR_LATER: 'saveForLater',
  SUBMIT_FOR_SELF: 'submitForSelf',
  SUBMIT_AND_DUPLICATE: 'submitAndDuplicate',
  ASSIGN_TO_STUDENTS: 'assignToStudents'
};

export const PRESENTATION_OPTIONS_VARIANT = {
  EXIT: 'exit',
  GRADEBOOK_SUBMIT: 'gradebookSubmit',
  COPY_FOR_STUDENTS: 'copyForStudents'
};

const OPTIONS_REQUIRING_ROSTER = new Set([
  PRESENTATION_OPTION.SUBMIT_AND_DUPLICATE,
  PRESENTATION_OPTION.ASSIGN_TO_STUDENTS
]);

const PresentationOptionsModal = observer((props) => {
  const {
    activityId = null,
    activityInstanceId = null,
    assignmentId = null,
    classroomId: classroomIdProp = null,
    onBackgroundRefresh = null,
    onClose,
    onComplete,
    onMount = null,
    open,
    t,
    variant = PRESENTATION_OPTIONS_VARIANT.EXIT
  } = props;

  const isGradebookSubmit = variant === PRESENTATION_OPTIONS_VARIANT.GRADEBOOK_SUBMIT;
  const isCopyForStudents = variant === PRESENTATION_OPTIONS_VARIANT.COPY_FOR_STUDENTS;
  const isAbridged = isGradebookSubmit || isCopyForStudents;

  const { assignmentManager, classroomManager, contentManager } = useContext(MobXProviderContext);

  const SCCheckbox = SatCoreComponent('SCCheckbox');
  const SCRadio = SatCoreComponent('SCRadio');

  const assignment = activityId ? assignmentManager.getAssignment(activityId) : null;
  const storedClassroomId = contentManager?.presentationContext?.classroomId;
  const classroomId = classroomIdProp
    || storedClassroomId
    || assignment?.classroomId
    || (assignment?.domainTypeId === 'classroom' ? assignment?.domainId : null)
    || classroomManager.currentClassroomId;

  const defaultOption = isCopyForStudents
    ? PRESENTATION_OPTION.SUBMIT_AND_DUPLICATE
    : (isGradebookSubmit
      ? PRESENTATION_OPTION.SUBMIT_FOR_SELF
      : PRESENTATION_OPTION.SAVE_FOR_LATER);

  const [selectedOption, setSelectedOption] = useState(defaultOption);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [alreadyCopiedStudentIds, setAlreadyCopiedStudentIds] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [completionResult, setCompletionResult] = useState(null);

  useEffect(() => {
    if (!open) {
      setSelectedOption(defaultOption);
      setSelectedStudentIds([]);
      setAlreadyCopiedStudentIds([]);
      setSubmitting(false);
      setShowSuccess(false);
      setCompletionResult(null);
      return;
    }

    if (isCopyForStudents) {
      setSelectedOption(PRESENTATION_OPTION.SUBMIT_AND_DUPLICATE);
    }

    const needsRoster = isCopyForStudents || OPTIONS_REQUIRING_ROSTER.has(selectedOption);
    if (showSuccess || !needsRoster || !classroomId) {
      return undefined;
    }

    let cancelled = false;
    const existingRoster = classroomManager.rosterMap.get(classroomId);
    const shouldFetchRoster = !existingRoster || existingRoster.length === 0;
    setRosterLoading(true);
    Promise.all([
      shouldFetchRoster ? classroomManager.fetchClassroomRoster(classroomId) : Promise.resolve(),
      activityId
        ? assignmentManager.fetchPresentationCopiedStudentIds(activityId)
        : Promise.resolve([])
    ]).then(([, copiedIds]) => {
      if (cancelled) {
        return;
      }
      const copied = Array.isArray(copiedIds) ? copiedIds : [];
      setAlreadyCopiedStudentIds(copied);
      // Keep already-copied students checked but disabled/greyed.
      setSelectedStudentIds((prev) => Array.from(new Set([...prev, ...copied])));
    }).finally(() => {
      if (!cancelled) {
        setRosterLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedOption, classroomId, showSuccess, isCopyForStudents, activityId]);

  const roster = classroomManager.rosterMap.get(classroomId) || [];
  const showRoster = isCopyForStudents || OPTIONS_REQUIRING_ROSTER.has(selectedOption);
  const selectableStudentIds = roster
    .map((student) => student.userId)
    .filter((userId) => !alreadyCopiedStudentIds.includes(userId));
  const selectedSelectableCount = selectedStudentIds.filter(
    (userId) => selectableStudentIds.includes(userId)
  ).length;
  const allSelectableSelected = selectableStudentIds.length > 0
    && selectedSelectableCount === selectableStudentIds.length;

  const handleOptionChange = (value) => {
    if (isCopyForStudents) {
      return;
    }
    setSelectedOption(value);
    if (!OPTIONS_REQUIRING_ROSTER.has(value)) {
      setSelectedStudentIds([]);
      setAlreadyCopiedStudentIds([]);
    }
  };

  const toggleStudent = (userId) => {
    if (alreadyCopiedStudentIds.includes(userId)) {
      return;
    }
    setSelectedStudentIds((prev) => (
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    ));
  };

  const toggleSelectAll = () => {
    if (allSelectableSelected) {
      setSelectedStudentIds([...alreadyCopiedStudentIds]);
    } else {
      setSelectedStudentIds([...new Set([...alreadyCopiedStudentIds, ...selectableStudentIds])]);
    }
  };

  const finish = (result = null) => {
    const completion = result || completionResult;
    setShowSuccess(false);
    setCompletionResult(null);
    onClose?.();
    if (completion) {
      onComplete?.(completion);
    }
  };

  const handleCancel = () => {
    if (showSuccess) {
      finish();
      return;
    }
    onClose?.();
    if (!isGradebookSubmit && !isCopyForStudents) {
      onComplete?.({ option: PRESENTATION_OPTION.SAVE_FOR_LATER });
    }
  };

  const submitSelf = async (instanceId) => {
    if (!instanceId) {
      return;
    }
    if (isGradebookSubmit) {
      await assignmentManager.teacherSubmitActivityInstance(instanceId);
    } else {
      await assignmentManager.submitActivityInstance(instanceId);
    }
  };

  const handleConfirm = async () => {
    if (submitting || showSuccess) {
      return;
    }

    if (selectedOption === PRESENTATION_OPTION.SAVE_FOR_LATER) {
      handleCancel();
      return;
    }

    setSubmitting(true);
    try {
      const newlySelectedStudentIds = selectedStudentIds.filter(
        (userId) => !alreadyCopiedStudentIds.includes(userId)
      );
      const context = {
        activityId,
        activityInstanceId: activityInstanceId || assignmentId,
        assignmentId,
        classroomId,
        selectedStudentIds: newlySelectedStudentIds
      };
      const result = { option: selectedOption, ...context };

      if (selectedOption === PRESENTATION_OPTION.SUBMIT_FOR_SELF) {
        await submitSelf(context.activityInstanceId);
        finish(result);
        return;
      }

      if (
        selectedOption === PRESENTATION_OPTION.SUBMIT_AND_DUPLICATE ||
        selectedOption === PRESENTATION_OPTION.ASSIGN_TO_STUDENTS
      ) {
        const shouldSubmitStudents = selectedOption === PRESENTATION_OPTION.SUBMIT_AND_DUPLICATE;

        // Already submitted/closed/graded presentations only need student copies.
        if (shouldSubmitStudents && !isCopyForStudents && context.activityInstanceId) {
          await submitSelf(context.activityInstanceId);
        }

        if (newlySelectedStudentIds.length > 0) {
          await assignmentManager.duplicatePresentationActivity(
            context.activityInstanceId,
            newlySelectedStudentIds,
            shouldSubmitStudents
          );
        }

        setCompletionResult(result);
        setShowSuccess(true);
        // Refresh gradebook while the success confirmation is shown.
        onBackgroundRefresh?.();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDisabled = submitting || (
    showRoster && selectedStudentIds.filter(
      (userId) => !alreadyCopiedStudentIds.includes(userId)
    ).length === 0
  );

  const exitOptions = [
    {
      value: PRESENTATION_OPTION.SAVE_FOR_LATER,
      label: t('saveForLater', 'Save for later'),
      description: t(
        'saveForLaterDescription',
        'The state of progress is saved and can be revisited. One entry under the teacher will appear in the Gradebook.'
      )
    },
    {
      value: PRESENTATION_OPTION.SUBMIT_FOR_SELF,
      label: t('submitForSelf', 'Submit for self'),
      description: t(
        'submitForSelfDescription',
        'Submit this content as finished. One entry under the teacher will appear in the Gradebook as completed.'
      )
    },
    {
      value: PRESENTATION_OPTION.SUBMIT_AND_DUPLICATE,
      label: t('submitAndDuplicate', 'Submit presentation, duplicate for students'),
      description: t(
        'submitAndDuplicateDescription',
        'Duplicate this work for all selected students and submit their entries as completed.'
      )
    },
    {
      value: PRESENTATION_OPTION.ASSIGN_TO_STUDENTS,
      label: t('assignToStudents', 'Assign to students'),
      description: t(
        'assignToStudentsDescription',
        'Create copies of this work for all selected students and make available for students to continue working independently.'
      )
    }
  ];

  const gradebookSubmitOptions = [
    {
      value: PRESENTATION_OPTION.SUBMIT_FOR_SELF,
      label: t('submitForSelf', 'Submit for self'),
      description: t(
        'submitForSelfDescription',
        'Submit this content as finished. One entry under the teacher will appear in the Gradebook as completed.'
      )
    },
    {
      value: PRESENTATION_OPTION.SUBMIT_AND_DUPLICATE,
      label: t('submitPresentationDuplicate', 'Submit presentation, duplicate for students'),
      description: t(
        'submitAndDuplicateDescription',
        'Duplicate this work for all selected students and submit their entries as completed.'
      )
    }
  ];

  const copyForStudentsOptions = [
    {
      value: PRESENTATION_OPTION.SUBMIT_AND_DUPLICATE,
      label: t('submitPresentationDuplicate', 'Submit presentation, duplicate for students'),
      description: t(
        'submitAndDuplicateDescription',
        'Duplicate this work for all selected students and submit their entries as completed.'
      )
    }
  ];

  const options = isCopyForStudents
    ? copyForStudentsOptions
    : (isGradebookSubmit ? gradebookSubmitOptions : exitOptions);
  const ModalBanner = SatCoreComponent('ModalBanner');
  // Match PresentAssignmentModal's z-index (see PresentationOptionsModal.less)
  // so this dialog replaces the Presentation Mode dialog.
  const dimmer = { className: 'presentation-options-dimmer' };

  if (showSuccess) {
    return (
      <Modal
        className={`presentation-options-modal success${isAbridged ? ' gradebook-submit' : ''}`}
        closeOnDimmerClick={false}
        dimmer={dimmer}
        onClose={handleCancel}
        open={open}
        size='small'>
        <ModalBanner
          label={t('successHeader', 'Success!')}
          onClose={handleCancel} />
        <Modal.Content>
          <p>
            {t(
              'duplicateSuccessMessage',
              'Entries for the selected students will be generated. This can take up to 2 minutes to appear in the Gradebook.'
            )}
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button
            className='saveButton'
            onClick={handleCancel}
            primary
            type='button'>
            {t('ok', 'OK')}
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }

  return (
    <Modal
      className={`presentation-options-modal${isAbridged ? ' gradebook-submit' : ''}`}
      closeOnDimmerClick={false}
      dimmer={dimmer}
      onClose={handleCancel}
      onMount={onMount}
      open={open}
      size='small'>
      <ModalBanner
        label={isGradebookSubmit
          ? t('submissionOptionsHeader', 'Submission Options')
          : t('presentationOptionsHeader', 'Presentation Options')}
        onClose={handleCancel} />
      <Modal.Content>
        <p className='presentation-options-prompt'>
          {t('presentationOptionsPrompt', 'What would you like to do with this presentation?')}
        </p>

        <div className='presentation-options-list'>
          {options.map((option) => (
            <div className='presentation-option-row' key={option.value}>
              <SCRadio
                checked={selectedOption === option.value}
                label={option.label}
                name='presentationOption'
                onChange={() => handleOptionChange(option.value)}
                value={option.value} />
              <div className='presentation-option-description'>
                {option.description}
              </div>
            </div>
          ))}
        </div>

        {showRoster && (
          <div className='presentation-options-roster'>
            <div className='presentation-options-roster-header'>
              {t(
                'selectedStudentsLabel',
                'Selected students to receive a copy of this content:'
              )}
            </div>
            {rosterLoading ? (
              <Loader active inline='centered' />
            ) : roster.length === 0 ? (
              <p className='presentation-options-roster-empty'>
                {t('noStudentsFound', 'No students found for this class.')}
              </p>
            ) : (
              <>
                <div className='presentation-options-select-all'>
                  <SCCheckbox
                    checked={allSelectableSelected}
                    disabled={selectableStudentIds.length === 0}
                    label={t('selectAll', 'Select / Deselect All Students')}
                    onChange={toggleSelectAll} />
                </div>
                <div className='presentation-options-roster-list'>
                  {roster.map((student) => {
                    const alreadyCopied = alreadyCopiedStudentIds.includes(student.userId);
                    return (
                      <div
                        className={`presentation-options-roster-item${alreadyCopied ? ' already-copied' : ''}`}
                        key={student.userId}>
                        <SCCheckbox
                          checked={selectedStudentIds.includes(student.userId)}
                          disabled={alreadyCopied}
                          label={`${student.lastName}, ${student.firstName}`}
                          onChange={() => toggleStudent(student.userId)}
                          value={student.userId} />
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button
          basic
          className='cancelButton'
          disabled={submitting}
          onClick={handleCancel}
          primary
          type='button'>
          {t('cancel', 'Cancel')}
        </Button>
        <Button
          className='saveButton'
          disabled={confirmDisabled}
          loading={submitting}
          onClick={handleConfirm}
          primary
          type='button'>
          {isAbridged
            ? t('submit', 'Submit')
            : t('saveAndExit', 'Save and Exit')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
});

export default PresentationOptionsModal;

SatCoreRegister('PresentationOptionsModal', PresentationOptionsModal);
